import React, { useState, useEffect, useCallback } from 'react';
import { db } from './firebase';
import {
  collection, onSnapshot, query, doc, getDoc, setDoc, getDocs, where, addDoc,
  serverTimestamp, increment, runTransaction
} from 'firebase/firestore';
import { BUSINESS_TYPES, getBusinessType } from './businessTypes';
import { Globe, RefreshCw } from 'lucide-react';

// kizunabd.com (WooCommerce) থেকে অর্ডার আনার জন্য Cloudflare Worker endpoint।
// WooCommerce-এর Consumer Key/Secret এখানে নেই — সেগুলো worker/index.js-এ
// সার্ভার-সাইড secret হিসেবে থাকে, ব্রাউজারে কখনো আসে না।
const WORKER_ENDPOINT = '/api/website-orders';
const WORKER_COMPLETE_ENDPOINT = '/api/website-orders/complete';

const formatTk = (num) => new Intl.NumberFormat('en-IN').format(Math.round(num || 0));

const isCashOnDelivery = (order) => {
  const method = `${order.paymentMethod || ''} ${order.paymentMethodTitle || ''}`.toLowerCase();
  return method.includes('cod') || method.includes('cash');
};

const formatOrderDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const date = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }).replace(/ /g, '-');
  const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  return `${date}, ${time}`;
};

const WebsiteOrders = () => {
  const [products, setProducts] = useState([]);
  const [secret, setSecret] = useState('');
  const [secretInput, setSecretInput] = useState('');
  const [orders, setOrders] = useState([]);
  const [orderState, setOrderState] = useState({}); // { [orderId]: { paid, deliveryPaidBy, matches: {itemIndex: productId}, importing } }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [activeDatePreset, setActiveDatePreset] = useState(null);

  useEffect(() => {
    const q = query(collection(db, 'products'));
    return onSnapshot(q, (snap) => {
      setProducts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
  }, []);

  useEffect(() => {
    getDoc(doc(db, 'settings', 'websiteSync')).then((snap) => {
      if (snap.exists()) setSecret(snap.data().secret || '');
    });
  }, []);

  const saveSecret = async () => {
    await setDoc(doc(db, 'settings', 'websiteSync'), { secret: secretInput.trim(), updatedAt: serverTimestamp() }, { merge: true });
    setSecret(secretInput.trim());
    setSecretInput('');
    alert('✅ Website Secret saved!');
  };

  // নাম মিলিয়ে ইনভেন্টরি প্রোডাক্টের সাথে ওয়েবসাইটের লাইন-আইটেম ম্যাচ করার চেষ্টা করে
  const findMatch = useCallback((name) => {
    const target = (name || '').toLowerCase().trim();
    return products.find((p) => (p.name || '').toLowerCase().trim() === target);
  }, [products]);

  const fetchOrders = async () => {
    if (!secret) return alert('Website Secret is not set yet — set it below first.');
    setLoading(true);
    setError('');
    try {
      const res = await fetch(WORKER_ENDPOINT, {
        method: 'POST',
        headers: { 'X-App-Secret': secret }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not fetch orders');

      // যেগুলো আগেই ইমপোর্ট করা হয়ে গেছে (sales collection-এ WEB-<number> ডকুমেন্ট আছে) সেগুলো বাদ।
      // Settings-এর "Reset Test Data" সেলগুলো মুছে দেয়, তাই তার আগে ইমপোর্ট হওয়া অর্ডারের নম্বর
      // settings/dataReset-এ রেখে যায় -- নাহলে সেগুলো আবার "নতুন অর্ডার" হয়ে ফিরে আসত।
      const resetSnap = await getDoc(doc(db, 'settings', 'dataReset'));
      const importedBeforeReset = new Set(resetSnap.exists() ? resetSnap.data().importedWebOrders || [] : []);
      const checked = await Promise.all(data.map(async (o) => {
        if (importedBeforeReset.has(`WEB-${o.number}`)) return null;
        const snap = await getDoc(doc(db, 'sales', `WEB-${o.number}`));
        return snap.exists() ? null : o;
      }));
      const fresh = checked.filter(Boolean);
      setOrders(fresh);

      const initialState = {};
      fresh.forEach((o) => {
        const matches = {};
        const bizTypes = {};
        o.items.forEach((item, idx) => {
          const m = findMatch(item.name);
          if (m) matches[idx] = m.id;
          // ম্যাচ পাওয়া গেলে সেই প্রোডাক্টের business type-ই ডিফল্ট ধরা হচ্ছে,
          // যাতে "Inventory Product" ড্রপডাউনে শুধু ওই একই ক্যাটাগরির প্রোডাক্ট দেখায়।
          bizTypes[idx] = m ? getBusinessType(m) : BUSINESS_TYPES[0];
        });
        initialState[o.id] = {
          paid: isCashOnDelivery(o) ? 0 : o.total,
          deliveryPaidBy: 'customer',
          courier: '', // '' | 'pathao' | 'steadfast' -- ঐচ্ছিক, Courier পেজে Book করার সময় আগে থেকে সিলেক্ট থাকবে
          matches,
          bizTypes,
          importing: false
        };
      });
      setOrderState(initialState);

      if (fresh.length === 0) alert('No new orders — everything is already imported.');
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const orderDateKey = (order) => {
    const d = new Date(order.dateCreated);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const applyDatePreset = (mode) => {
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    if (mode === 'today') {
      setStartDate(todayStr); setEndDate(todayStr);
    } else if (mode === 'month') {
      setStartDate(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`);
      setEndDate(todayStr);
    }
    setActiveDatePreset(mode);
  };

  const clearDateFilter = () => {
    setStartDate(''); setEndDate(''); setActiveDatePreset(null);
  };

  const filteredOrders = orders.filter((o) => {
    if (!startDate && !endDate) return true;
    const key = orderDateKey(o);
    return (!startDate || key >= startDate) && (!endDate || key <= endDate);
  });

  const updateOrderState = (orderId, patch) => {
    setOrderState((prev) => ({ ...prev, [orderId]: { ...prev[orderId], ...patch } }));
  };

  const updateMatch = (orderId, itemIndex, productId) => {
    setOrderState((prev) => ({
      ...prev,
      [orderId]: { ...prev[orderId], matches: { ...prev[orderId].matches, [itemIndex]: productId } }
    }));
  };

  // Business type বদলালে সেই লাইনের প্রোডাক্ট-সিলেকশন রিসেট হয়ে যাবে, কারণ
  // আগের সিলেক্ট করা প্রোডাক্টটা নতুন category-তে নাও থাকতে পারে।
  const updateBizType = (orderId, itemIndex, businessType) => {
    setOrderState((prev) => ({
      ...prev,
      [orderId]: {
        ...prev[orderId],
        bizTypes: { ...prev[orderId].bizTypes, [itemIndex]: businessType },
        matches: { ...prev[orderId].matches, [itemIndex]: '' }
      }
    }));
  };

  const importOrder = async (order) => {
    const state = orderState[order.id];
    const unmatchedIdx = order.items.findIndex((_, idx) => !state.matches[idx]);
    if (unmatchedIdx !== -1) {
      return alert(`"${order.items[unmatchedIdx].name}" wasn't found in inventory. Please select it manually.`);
    }

    updateOrderState(order.id, { importing: true });
    try {
      const invoiceId = `WEB-${order.number}`;
      const items = order.items.map((item, idx) => {
        const product = products.find((p) => p.id === state.matches[idx]);
        return {
          id: product.id,
          name: product.name,
          category: product.category || '',
          businessType: getBusinessType(product),
          price: item.price,
          costPrice: product.costPrice || 0,
          qty: item.qty,
          itemDiscountType: 'tk',
          itemDiscountValue: 0
        };
      });

      const subtotal = items.reduce((sum, it) => sum + it.price * it.qty, 0);
      const deliveryCharge = Math.round(order.shippingTotal || 0);
      const customerPaysDelivery = state.deliveryPaidBy !== 'shop';
      const paid = Math.max(0, Number(state.paid) || 0);
      const totalBill = Math.round(order.total || (subtotal + (customerPaysDelivery ? deliveryCharge : 0)));
      const dueAmount = paid < totalBill ? Math.round(totalBill - paid) : 0;
      const returnAmount = paid > totalBill ? Math.round(paid - totalBill) : 0;

      const saleDoc = {
        invoiceId,
        name: order.customer.name || 'Website Customer',
        phone: order.customer.phone || '',
        phone2: '',
        address: order.customer.address || '',
        soldBy: 'Website',
        source: 'website',
        wcOrderId: order.id,
        wcOrderNumber: order.number,
        items: items.map((it) => ({
          ...it,
          finalItemTotal: Math.round(it.price * it.qty),
          itemDiscAmount: 0,
          itemDiscountText: '৳ 0'
        })),
        subtotal: Math.round(subtotal),
        itemDiscountTotal: 0,
        overallDiscountType: 'tk',
        overallDiscountVal: 0,
        overallDiscountAmount: 0,
        totalDiscount: 0,
        deliveryCharge,
        deliveryMethod: 'courier', // website orders need shipping to the customer, unlike walk-in POS sales
        // কোন কুরিয়ারে দেওয়া হবে -- এটা শুধু পছন্দ; আসল Booking হয় Courier পেজে
        ...(state.courier ? { preferredCourier: state.courier } : {}),
        deliveryPaidBy: customerPaysDelivery ? 'customer' : 'shop',
        deliveryPaidByName: customerPaysDelivery ? (order.customer.name || 'Customer') : 'Kizuna',
        totalBill,
        paid,
        dueAmount,
        returnAmount,
        dateTime: formatOrderDate(order.dateCreated)
      };

      if (order.customer.phone) {
        const custSnap = await getDocs(query(collection(db, 'customers'), where('phone', '==', order.customer.phone)));
        if (custSnap.empty) {
          await addDoc(collection(db, 'customers'), {
            name: saleDoc.name, phone: saleDoc.phone, phone2: '', address: saleDoc.address, createdAt: serverTimestamp()
          });
        }
      }

      await runTransaction(db, async (transaction) => {
        const saleRef = doc(db, 'sales', invoiceId);
        const saleSnap = await transaction.get(saleRef);
        if (saleSnap.exists()) return;
        transaction.set(saleRef, { ...saleDoc, createdAt: serverTimestamp() });
        items.forEach((item) => {
          transaction.update(doc(db, 'products', item.id), { stock: increment(-item.qty) });
          transaction.set(doc(db, 'productStats', item.id), { name: item.name, qtySold: increment(item.qty) }, { merge: true });
        });
      });

      setOrders((prev) => prev.filter((o) => o.id !== order.id));

      // Firestore-এ সেল সেভ হয়ে গেছে -- এটাই আসল রেকর্ড। WooCommerce-এ Completed
      // মার্ক করাটা শুধু sync-এর সুবিধার্থে, এটা ব্যর্থ হলেও ইমপোর্ট সফলই ধরা হবে।
      let wcSyncWarning = '';
      try {
        const completeRes = await fetch(WORKER_COMPLETE_ENDPOINT, {
          method: 'POST',
          headers: { 'X-App-Secret': secret, 'content-type': 'application/json' },
          body: JSON.stringify({ orderId: order.id })
        });
        if (!completeRes.ok) {
          const completeData = await completeRes.json().catch(() => ({}));
          wcSyncWarning = `\n\n(Note: the sale was recorded, but marking it Completed on the website failed: ${completeData.error || completeRes.status}. This usually means the WooCommerce API key needs Read/Write permission instead of Read-only.)`;
        }
      } catch (err) {
        wcSyncWarning = '\n\n(Note: the sale was recorded, but could not reach the website to mark it Completed.)';
      }

      alert(`✅ Order #${order.number} imported successfully!${wcSyncWarning}`);
    } catch (err) {
      alert('Error: ' + err.message);
      updateOrderState(order.id, { importing: false });
    }
  };

  return (
    <div style={{ padding: '10px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Globe color="#4a00e0" />
          <h2 style={{ margin: 0, color: '#333' }}>Website Orders (kizunabd.com)</h2>
        </div>
        <button
          onClick={fetchOrders}
          disabled={loading}
          className="btn-confirm"
          style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 18px', opacity: loading ? 0.7 : 1 }}
        >
          <RefreshCw size={16} className={loading ? 'spin' : ''} /> {loading ? 'Fetching...' : 'Fetch Website Orders'}
        </button>
      </div>

      {error && <div style={{ background: '#ffeded', color: 'red', padding: '10px 15px', borderRadius: '10px', marginBottom: '15px' }}>{error}</div>}

      <div className="white-box" style={{ marginBottom: '20px', padding: '15px 20px' }}>
        <b style={{ fontSize: '13px', color: '#666' }}>Website Sync Setup</b>
        <p style={{ fontSize: '12px', color: '#999', margin: '6px 0 10px' }}>
          This secret must exactly match the <code>APP_SHARED_SECRET</code> set on the Cloudflare Worker.
          {secret ? ' (currently set ✅)' : ' (not set yet ⚠️)'}
        </p>
        <div style={{ display: 'flex', gap: '10px' }}>
          <input
            type="password"
            placeholder="Enter new Shared Secret"
            value={secretInput}
            onChange={(e) => setSecretInput(e.target.value)}
            className="input-field"
            style={{ flex: 1 }}
          />
          <button onClick={saveSecret} disabled={!secretInput.trim()} className="btn-primary" style={{ padding: '0 20px' }}>Save</button>
        </div>
      </div>

      {orders.length === 0 && !loading && (
        <div style={{ textAlign: 'center', color: '#999', padding: '40px' }}>
          Click "Fetch Website Orders" to bring in new orders from the website.
        </div>
      )}

      {orders.length > 0 && (
        <div className="white-box" style={{ marginBottom: '18px', padding: '15px 20px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <b style={{ fontSize: '13px', color: '#666', marginRight: '4px' }}>
            📅 {filteredOrders.length}/{orders.length} order{orders.length === 1 ? '' : 's'}
          </b>
          {[{ key: 'today', label: 'Today' }, { key: 'month', label: 'This Month' }].map((p) => (
            <button
              key={p.key}
              onClick={() => applyDatePreset(p.key)}
              style={{
                padding: '6px 12px', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold',
                border: `1px solid ${activeDatePreset === p.key ? '#4a00e0' : '#ddd'}`,
                background: activeDatePreset === p.key ? '#4a00e0' : '#fff',
                color: activeDatePreset === p.key ? '#fff' : '#333'
              }}
            >
              {p.label}
            </button>
          ))}
          <input type="date" className="input-field" value={startDate} onChange={(e) => { setStartDate(e.target.value); setActiveDatePreset(null); }} style={{ width: '150px', marginBottom: 0 }} />
          <span style={{ color: '#999', fontSize: '12px' }}>to</span>
          <input type="date" className="input-field" value={endDate} onChange={(e) => { setEndDate(e.target.value); setActiveDatePreset(null); }} style={{ width: '150px', marginBottom: 0 }} />
          {(startDate || endDate) && (
            <button onClick={clearDateFilter} style={{ padding: '6px 12px', background: '#e74c3c', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' }}>Clear</button>
          )}
        </div>
      )}

      {orders.length > 0 && filteredOrders.length === 0 && (
        <div style={{ textAlign: 'center', color: '#999', padding: '30px' }}>
          No orders in this date range.
        </div>
      )}

      {filteredOrders.map((order) => {
        const state = orderState[order.id] || {};
        const cod = isCashOnDelivery(order);
        return (
          <div key={order.id} className="white-box" style={{ marginBottom: '18px', padding: '18px 20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px', marginBottom: '10px' }}>
              <div>
                <b style={{ color: '#4a00e0' }}>Order #{order.number}</b>
                <span style={{ marginLeft: '10px', fontSize: '12px', color: '#888' }}>{formatOrderDate(order.dateCreated)}</span>
                <span style={{
                  marginLeft: '10px', fontSize: '11px', padding: '2px 8px', borderRadius: '10px', fontWeight: 'bold',
                  background: cod ? '#fff3cd' : '#eafff3', color: cod ? '#8a6d3b' : '#218c61'
                }}>
                  {cod ? 'Cash on Delivery' : (order.paymentMethodTitle || 'Paid Online')}
                </span>
              </div>
              <div style={{ fontSize: '13px', color: '#333' }}>
                <b>{order.customer.name || 'Website Customer'}</b> {order.customer.phone && `| ${order.customer.phone}`}
                {order.customer.address && <div style={{ fontSize: '12px', color: '#888' }}>{order.customer.address}</div>}
              </div>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '10px' }}>
              <thead>
                <tr style={{ background: '#f4f7fe' }}>
                  <th style={{ textAlign: 'left', padding: '6px 8px', fontSize: '12px' }}>Website Item</th>
                  <th style={{ fontSize: '12px' }}>Qty</th>
                  <th style={{ fontSize: '12px' }}>Price</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', fontSize: '12px' }}>Category</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', fontSize: '12px' }}>Inventory Product</th>
                </tr>
              </thead>
              <tbody>
                {order.items.map((item, idx) => {
                  const matched = state.matches?.[idx];
                  const bizType = state.bizTypes?.[idx] || BUSINESS_TYPES[0];
                  const optionsForType = products.filter((p) => getBusinessType(p) === bizType);
                  return (
                    <tr key={idx} style={{ borderBottom: '1px solid #f9f9f9' }}>
                      <td style={{ padding: '6px 8px', fontSize: '13px' }}>{item.name}</td>
                      <td align="center" style={{ fontSize: '13px' }}>{item.qty}</td>
                      <td align="center" style={{ fontSize: '13px' }}>৳ {formatTk(item.price)}</td>
                      <td style={{ padding: '6px 8px' }}>
                        <select
                          value={bizType}
                          onChange={(e) => updateBizType(order.id, idx, e.target.value)}
                          style={{ padding: '4px', borderRadius: '6px', fontSize: '12px', border: '1px solid #ddd' }}
                        >
                          {BUSINESS_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <select
                          value={matched || ''}
                          onChange={(e) => updateMatch(order.id, idx, e.target.value)}
                          style={{
                            padding: '4px', borderRadius: '6px', fontSize: '12px', width: '100%',
                            border: matched ? '1px solid #ddd' : '1px solid red'
                          }}
                        >
                          <option value="">-- Select --</option>
                          {optionsForType.map((p) => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </select>
                        {!matched && <span style={{ fontSize: '10px', color: 'red' }}>No match found!</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap', background: '#f8f9ff', padding: '10px 15px', borderRadius: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '13px' }}>Delivery Charge: ৳ {formatTk(order.shippingTotal)}</span>
                <span style={{ fontSize: '11px', color: '#888' }}>Paid by:</span>
                <button type="button" onClick={() => updateOrderState(order.id, { deliveryPaidBy: 'customer' })}
                  style={{ padding: '3px 8px', borderRadius: '5px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer', border: `1px solid ${state.deliveryPaidBy !== 'shop' ? '#10b981' : '#ddd'}`, background: state.deliveryPaidBy !== 'shop' ? '#eafff3' : '#fff', color: state.deliveryPaidBy !== 'shop' ? '#218c61' : '#999' }}>Customer</button>
                <button type="button" onClick={() => updateOrderState(order.id, { deliveryPaidBy: 'shop' })}
                  style={{ padding: '3px 8px', borderRadius: '5px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer', border: `1px solid ${state.deliveryPaidBy === 'shop' ? '#e74c3c' : '#ddd'}`, background: state.deliveryPaidBy === 'shop' ? '#ffebee' : '#fff', color: state.deliveryPaidBy === 'shop' ? '#c0392b' : '#999' }}>Kizuna</button>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '13px' }}>Paid Amount:</span>
                <input
                  type="number"
                  value={state.paid}
                  onFocus={(e) => e.target.select()}
                  onChange={(e) => updateOrderState(order.id, { paid: e.target.value })}
                  style={{ width: '100px', padding: '5px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '12px' }}
                />
                {cod && <span style={{ fontSize: '11px', color: '#888' }}>(enter amount once collected on delivery)</span>}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '13px' }}>Courier:</span>
                {[{ key: 'pathao', label: 'Pathao' }, { key: 'steadfast', label: 'Steadfast' }].map((c) => {
                  const active = state.courier === c.key;
                  return (
                    <button key={c.key} type="button"
                      onClick={() => updateOrderState(order.id, { courier: active ? '' : c.key })}
                      style={{ padding: '3px 8px', borderRadius: '5px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer', border: `1px solid ${active ? '#4a00e0' : '#ddd'}`, background: active ? '#f4f0ff' : '#fff', color: active ? '#4a00e0' : '#999' }}>{c.label}</button>
                  );
                })}
                <span style={{ fontSize: '11px', color: '#888' }}>(booked later on the Courier page)</span>
              </div>

              <b style={{ fontSize: '14px', marginLeft: 'auto' }}>Total: ৳ {formatTk(order.total)}</b>

              <button
                onClick={() => importOrder(order)}
                disabled={state.importing}
                className="btn-confirm"
                style={{ padding: '8px 20px', fontSize: '13px', opacity: state.importing ? 0.6 : 1 }}
              >
                {state.importing ? 'Importing...' : 'Import This Order'}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default WebsiteOrders;
